import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { switchMap, map, tap, Observable } from 'rxjs';
import { ApplicationState } from '../../../../../core/models/application-state/application-state';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { ApplicationStateService } from '../../../shared/services/application-state/application-state.service';
import { ApplicationTemplateService } from '../../../shared/services/application-template/application-template.service';
import { ApplicationService } from '../../../shared/services/application/application.service';
import { ApplicationTemplateExistsRequest } from '../../shared/models/application-template-exists-request';
import { ApplicationTemplateRequest } from '../../shared/models/application-template-form-model';
import { ApplicationTemplateResponse } from '../../shared/models/application-template-response';
import { ApplicationTemplateFormService } from '../../shared/services/application-template-form/application-template-form.service';
import { ApplicationTemplateFormDialogComponent } from '../application-template-form-dialog/application-template-form-dialog.component';

@Component({
    selector: 'app-application-template-modification-form',
    templateUrl: `./application-template-modification-form.component.html`,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ApplicationTemplateFormDialogComponent],
})
export class ApplicationTemplateModificationFormComponent implements OnInit {
    applicationState: ApplicationState | null =
        this.applicationStateService.getCurrentApplicationState();

    form: FormGroup<FormGroupType<ApplicationTemplateRequest>> =
        this.applicationTemplateFormService.getTemplateForm();

    constructor(
        private readonly applicationTemplateFormService: ApplicationTemplateFormService,
        private readonly applicationTemplateService: ApplicationTemplateService,
        private readonly applicationStateService: ApplicationStateService,
        private readonly applicationService: ApplicationService,
        private readonly dialogRef: MatDialogRef<ApplicationTemplateModificationFormComponent>,
    ) {}

    ngOnInit(): void {
        this.loadForm();
    }

    saveTemplate(): void {
        this.applicationTemplateService
            .updateTemplate(
                this.applicationState!.applicationTemplateInformation!
                    .applicationTemplateId,
                this.generateRequestObject(),
            )
            .pipe(
                switchMap((applicationTemplateResponse) =>
                    this.applicationService
                        .getApplicationState(
                            this.applicationState!.applicationId.toString(),
                        )
                        .pipe(map(() => applicationTemplateResponse)),
                ),
            )
            .subscribe((applicationTemplateResponse) => {
                this.closeDialog(applicationTemplateResponse);
            });
    }

    closeDialog(
        applicationTemplateResponse: ApplicationTemplateResponse | null = null,
    ): void {
        this.dialogRef.close(applicationTemplateResponse);
    }

    checkTemplateNameExistence(): void {
        this.applicationTemplateFormService.checkTemplateNameExistence(
            this.form,
            this.generateApplicationTemplateExistsRequest(),
        );
    }

    private loadForm(): void {
        this.getFormDataFromApi(
            this.applicationState!.applicationTemplateInformation!
                .applicationTemplateId,
        )
            .pipe(
                tap((response: ApplicationTemplateResponse) => {
                    this.bindForm(response);
                }),
                map(() => void 0),
            )
            .subscribe();
    }

    private bindForm(response: ApplicationTemplateResponse): void {
        this.form.patchValue(response);
    }

    private getFormDataFromApi(
        applicationTemplateId: number,
    ): Observable<ApplicationTemplateResponse> {
        return this.applicationTemplateService.get(applicationTemplateId);
    }

    private generateRequestObject(): ApplicationTemplateRequest {
        const formValue = this.form
            .value as Required<ApplicationTemplateRequest>;

        return formValue;
    }

    private generateApplicationTemplateExistsRequest(): ApplicationTemplateExistsRequest {
        return {
            applicationTemplateId:
                this.applicationState!.applicationTemplateInformation!
                    .applicationTemplateId,
            name: this.form.controls.name.value!,
        };
    }
}
